import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { reminderService } from '../services/consultations/reminderService';
import { ConsultationReminderPopup } from '../components/consultations/ConsultationReminderPopup';

interface ConsultationReminder {
  id: string;
  clientName: string;
  consultationType: 'in-person' | 'virtual';
  date: Date;
  time: string;
  location?: string;
  meetLink?: string;
  notes?: string;
}

interface ReminderContextType {
  activeReminders: ConsultationReminder[];
  dismissReminder: (id: string) => void;
}

const ReminderContext = createContext<ReminderContextType>({
  activeReminders: [],
  dismissReminder: () => {},
});

export const useReminders = () => useContext(ReminderContext);

export const ReminderProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentUser, userProfile } = useAuth();
  const [activeReminders, setActiveReminders] = useState<ConsultationReminder[]>([]);

  const dismissReminder = useCallback((id: string) => {
    setActiveReminders(prev => prev.filter(r => r.id !== id));
  }, []);

  const handleInAppReminder = useCallback((consultation: any) => {
    const reminder: ConsultationReminder = {
      id: consultation.id,
      clientName: consultation.clientName,
      consultationType: consultation.consultationType,
      date: consultation.date,
      time: consultation.time,
      location: consultation.location,
      meetLink: consultation.meetLink,
      notes: consultation.notes,
    };

    setActiveReminders(prev => {
      // Check if this reminder is already active
      if (prev.some(r => r.id === reminder.id)) {
        return prev;
      }
      return [...prev, reminder];
    });
  }, []);

  useEffect(() => {
    if (!currentUser || !userProfile) {
      return;
    }

    // Start reminder checking service
    const cleanup = reminderService.startReminderChecking(
      currentUser.uid,
      handleInAppReminder
    );

    // Also check for 24-hour reminders once on mount
    reminderService.check24HourReminders(currentUser.uid);

    return cleanup;
  }, [currentUser, userProfile, handleInAppReminder]);

  return (
    <ReminderContext.Provider value={{ activeReminders, dismissReminder }}>
      {children}
      {/* Render active reminder popups */}
      {activeReminders.map(reminder => (
        <ConsultationReminderPopup
          key={reminder.id}
          reminder={reminder}
          onDismiss={dismissReminder}
        />
      ))}
    </ReminderContext.Provider>
  );
};